import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { List, XCircle } from "@phosphor-icons/react";

const LOGO_URL = "/gjc-logo-light-clean.svg";

const navLinks = [
  { key: "home", path: "/" },
  { key: "about", path: "/about" },
  { key: "solutions", path: "/solutions" },
  { key: "markets", path: "/markets" },
  { key: "whyGJC", path: "/why-gjc" },
  { key: "contact", path: "/contact" },
];

export default function Header() {
  const { lang, toggleLang, t } = useLang();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="main-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" data-testid="header-logo" className="flex items-center shrink-0">
          <img
            src={LOGO_URL}
            alt="GJC Enertec"
            className="w-[185px] md:w-[245px] h-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map(({ key, path }) => {
            const isActive = location.pathname === path;
            return (
              <Link
                key={key}
                to={path}
                data-testid={`nav-link-${key}`}
                className={`text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "text-[#0D9488]"
                    : "text-slate-700 hover:text-[#0D9488]"
                }`}
              >
                {t.nav[key]}
              </Link>
            );
          })}
        </nav>

        {/* Right side: Lang toggle + CTA */}
        <div className="flex items-center gap-4">
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className="text-xs font-bold uppercase tracking-wider border border-slate-300 px-3 py-1.5 hover:border-[#0D9488] hover:text-[#0D9488] transition-colors duration-200"
            aria-label={`Switch to ${lang === "en" ? "Spanish" : "English"}`}
          >
            {lang === "en" ? "ES" : "EN"}
          </button>

          <Link
            to="/contact"
            data-testid="header-cta"
            className="hidden md:inline-flex bg-[#0A192F] text-white px-5 py-2 text-sm font-medium hover:bg-[#0D9488] transition-colors duration-300"
          >
            {t.nav.talkToUs}
          </Link>

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-slate-700"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <XCircle size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <nav
          data-testid="mobile-nav"
          className="lg:hidden bg-white border-t border-slate-200 px-6 py-6 space-y-4"
        >
          {navLinks.map(({ key, path }) => {
            const isActive = location.pathname === path;
            return (
              <Link
                key={key}
                to={path}
                data-testid={`mobile-nav-link-${key}`}
                onClick={() => setMobileOpen(false)}
                className={`block text-base font-medium ${
                  isActive ? "text-[#0D9488]" : "text-slate-700"
                }`}
              >
                {t.nav[key]}
              </Link>
            );
          })}
          <Link
            to="/contact"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-cta"
            className="block bg-[#0A192F] text-white text-center px-5 py-3 text-sm font-medium mt-4"
          >
            {t.nav.talkToUs}
          </Link>
        </nav>
      )}
    </header>
  );
}
